import queryString from "query-string";

export interface FilterOption {
  value: string;
  label: string;
  color?: string;
}

export interface FilterState {
  q: string;
  page: number;
  selectedStates: string[];
  selectedProviders?: string[];
  selectedProducts?: string[];
  selectedAssignedTo?: string[];
  selectedRequestedBy?: string[];
  selectedTypes?: string[];
  selectedTransferTypes?: string[];
  sortField?: string;
  sortDirection?: string;
}

export interface QueryStringOptions {
  includeSort?: boolean;
  includeAssignedTo?: boolean;
  includeRequestedBy?: boolean;
  includeType?: boolean;
  includeTransferType?: boolean;
  includeProviders?: boolean;
  includeProducts?: boolean;
}

export const extractPageFromQuery = (
  page: string | string[] | null | undefined | number,
): number => {
  if (Array.isArray(page)) {
    return parseInt(page[0] || "1", 10);
  }
  return parseInt((page as string) || "1", 10);
};

export const parseQueryParams = (
  options: QueryStringOptions = {}
): Partial<FilterState> => {
  const parsed = queryString.parse(window.location.search);

  const result: Partial<FilterState> = {
    q: (parsed.q as string) || "",
    page: extractPageFromQuery(parsed.page as string),
    selectedStates: parsed.states
      ? (parsed.states as string).split(",")
      : [],
  };

  if (options.includeAssignedTo) {
    result.selectedAssignedTo = parsed.assignedTo
      ? (parsed.assignedTo as string).split(",")
      : [];
  }

  if (options.includeRequestedBy) {
    result.selectedRequestedBy = parsed.requestedBy
      ? (parsed.requestedBy as string).split(",")
      : [];
  }

  if (options.includeType) {
    result.selectedTypes = parsed.types
      ? (parsed.types as string).split(",")
      : [];
  }

  if (options.includeTransferType) {
    result.selectedTransferTypes = parsed.transferTypes
      ? (parsed.transferTypes as string).split(",")
      : [];
  }

  if (options.includeProviders) {
    result.selectedProviders = parsed.providers
      ? (parsed.providers as string).split(",")
      : [];
  }

  if (options.includeProducts) {
    result.selectedProducts = parsed.products
      ? (parsed.products as string).split(",")
      : [];
  }

  if (options.includeSort) {
    result.sortField = (parsed.sortField as string) || "CREATED_AT";
    result.sortDirection = (parsed.sortDirection as string) || "DESC";
  }

  return result;
};

export const clearAllFilters = (context: any, modelName?: string) => {
  const { history, location } = context.props;
  const model = modelName || context.props.model;

  context.setState(
    {
      q: "",
      page: 1,
      selectedStates: [],
      selectedRequestedBy: [],
      selectedAssignedTo: [],
      selectedTypes: [],
      selectedProviders: [],
      selectedProducts: [],
      selectedTransferTypes: [],
      sortField: "CREATED_AT",
      sortDirection: "DESC",
      selectedFieldsToDisplay: [],
      removedField: undefined,
    },
    () => {
      if (model) {
        localStorage.removeItem(model);
      }
      if (history && location) {
        history.replace(location.pathname);
      }

      const searchTrigger = 
        context.triggerImmediateSearch || 
        context.performSearch || 
        context.getOnlineTransactions;

      if (searchTrigger && typeof searchTrigger === 'function') {
        searchTrigger.call(context);
      }
    }
  );
};

export const buildQueryString = (
  state: FilterState,
  options: QueryStringOptions = {}
): string => {
  const queryStrings: string[] = [];

  if (state.q) {
    queryStrings.push(`q=${state.q}`);
  }

  if (state.selectedStates.length > 0) {
    queryStrings.push(`states=${state.selectedStates.join(",")}`);
  }

  if (options.includeAssignedTo && state.selectedAssignedTo?.length) {
    queryStrings.push(`assignedTo=${state.selectedAssignedTo.join(",")}`);
  }

  if (options.includeRequestedBy && state.selectedRequestedBy?.length) {
    queryStrings.push(`requestedBy=${state.selectedRequestedBy.join(",")}`);
  }

  if (options.includeType && state.selectedTypes?.length) {
    queryStrings.push(`types=${state.selectedTypes.join(",")}`);
  }

  if (options.includeTransferType && state.selectedTransferTypes?.length) {
    queryStrings.push(`transferTypes=${state.selectedTransferTypes.join(",")}`);
  }

  if (options.includeProviders && state.selectedProviders?.length) {
    queryStrings.push(`providers=${state.selectedProviders.join(",")}`);
  }

  if (options.includeProducts && state.selectedProducts?.length) {
    queryStrings.push(`products=${state.selectedProducts.join(",")}`);
  }

  if (options.includeSort) {
    if (state.sortField && state.sortField !== "CREATED_AT") {
      queryStrings.push(`sortField=${state.sortField}`);
    }

    if (state.sortDirection && state.sortDirection !== "DESC") {
      queryStrings.push(`sortDirection=${state.sortDirection}`);
    }
  }

  queryStrings.push(`page=${state.page}`);

  return `?${queryStrings.join("&")}`;
};

export const hasActiveFilters = (
  state: FilterState,
  options: QueryStringOptions = {}
): boolean => {
  if (state.q || state.selectedStates.length > 0) {
    return true;
  }

  if (options.includeAssignedTo && state.selectedAssignedTo?.length) {
    return true;
  }

  if (options.includeRequestedBy && state.selectedRequestedBy?.length) {
    return true;
  }

  if (options.includeType && state.selectedTypes?.length) {
    return true;
  }

  if (options.includeTransferType && state.selectedTransferTypes?.length) {
    return true;
  }

  if (options.includeProviders && state.selectedProviders?.length) {
    return true;
  }

  if (options.includeProducts && state.selectedProducts?.length) {
    return true;
  }

  if (options.includeSort) {
    if (state.sortField !== "CREATED_AT" || state.sortDirection !== "DESC") {
      return true;
    }
  }

  return false;
};

export const handleFieldRemove = (context: any, field: any, model: string) => {
  const fields = context.state.selectedFieldsToDisplay.filter(
    (f: any) => f.name !== field.name,
  );
  context.setState(
    { removedField: field, selectedFieldsToDisplay: fields },
    () => {
      localStorage.setItem(model, JSON.stringify(fields));

      const searchTrigger =
        context.triggerImmediateSearch ||
        context.performSearch ||
        context.getOnlineTransactions;

      if (searchTrigger && typeof searchTrigger === "function") {
        searchTrigger.call(context);
      }
    },
  );
};
